import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Suspense, lazy } from "react";

import Link from "ds/components/Link";
import CodeSnippetInline from "ds/components/CodeSnippetInline";
import CodeSnippetBlock from "ds/components/CodeSnippetBlock";

import styles from "./styles.module.css";

const SyntaxHighlighter = lazy(() => import("../SyntaxHighlighter"));

const components: Partial<Components> = {
  code({ children, className }) {
    // Inline code - no className means it's not a code block
    if (!className) {
      return <CodeSnippetInline>{String(children).trim()}</CodeSnippetInline>;
    }
    // Tolerate malformed fences whose info string already includes a
    // "language-" prefix (e.g. ```language-hcl), which react-markdown turns
    // into className="language-language-hcl".
    const match = /(?:language-)+(\w+)/.exec(className);
    const language = match?.[1];
    const codeString = String(children).trim();

    // Code block without syntax highlighting
    if (!language) {
      return (
        <CodeSnippetBlock className={styles.codeBlock} allowCopy copyTitle="Copy code">
          {codeString}
        </CodeSnippetBlock>
      );
    }

    // Code block with syntax highlighting
    return (
      <CodeSnippetBlock
        className={styles.codeBlock}
        language={language}
        allowCopy
        copyTitle="Copy code"
        copyValue={codeString}
      >
        <Suspense fallback={<code>{codeString}</code>}>
          <SyntaxHighlighter value={codeString} language={language} />
        </Suspense>
      </CodeSnippetBlock>
    );
  },
  a({ children, href }) {
    // Internal link
    if (href.startsWith("/")) {
      return <Link href={href}>{children}</Link>;
    }

    // External link
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
};

type MarkdownAssistantPanelProps = {
  children: string;
};

const MarkdownAssistantPanel = ({ children }: MarkdownAssistantPanelProps) => {
  return (
    <section className={styles.markdown}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} skipHtml>
        {children}
      </ReactMarkdown>
    </section>
  );
};

export default MarkdownAssistantPanel;
